import type { LeadSignalTimelineDto, CompanySignalDto } from '@/api/leads'

/** Keys `getLeadComparator` has a real ordering for. Anything else it declines (see below). */
const LEAD_SORT_KEYS = new Set([
  'lead', 'phone', 'enrichment', 'company', 'title', 'attributionSource',
  'webinarAttended', 'lastConversion', 'conversions', 'signal',
])

/**
 * Get a comparator function for sorting leads by a specific key, or `undefined` for a key this does
 * not order.
 *
 * Declining matters: DataGrid falls through to the column's own accessor when the caller returns
 * nothing, and hides the sort control when neither exists. A `default: return 0` arm would answer for
 * every key, so a new sortable column added without a case here would draw a live arrow over a list
 * that never moves — which is exactly the bug ORB-715 removed.
 */
export function getLeadComparator(key: string) {
  if (!LEAD_SORT_KEYS.has(key)) return undefined
  return (a: LeadSignalTimelineDto, b: LeadSignalTimelineDto): number => {
    const getName = (l: LeadSignalTimelineDto) =>
      l.FullName || [l.FirstName, l.LastName].filter(Boolean).join(' ') || l.Email || ''

    switch (key) {
      case 'lead':
        return getName(a).localeCompare(getName(b))
      case 'phone':
        return (a.Phone || a.GlobalContactPhone || '').localeCompare(b.Phone || b.GlobalContactPhone || '')
      case 'enrichment':
        return (a.EnrichmentStatus || '').localeCompare(b.EnrichmentStatus || '')
      case 'company':
        return (a.Company || '').localeCompare(b.Company || '')
      case 'title':
        return (a.JobTitle || '').localeCompare(b.JobTitle || '')
      case 'attributionSource':
        return (a.WebSourceAttributions?.[0]?.Source || '').localeCompare(b.WebSourceAttributions?.[0]?.Source || '')
      case 'webinarAttended':
        return (a.WebinarAttended === b.WebinarAttended) ? 0 : (a.WebinarAttended ? -1 : 1)
      case 'lastConversion':
        return (a.LastInteractionAt || '').localeCompare(b.LastInteractionAt || '')
      case 'conversions':
        return a.InteractionCount - b.InteractionCount
      case 'signal':
        return a.SignalScore - b.SignalScore
      default:
        return 0
    }
  }
}

/** Keys `getCompanyComparator` has a real ordering for. See `getLeadComparator` on why it declines. */
const COMPANY_SORT_KEYS = new Set([
  'company', 'industry', 'size', 'lastConversion', 'leads', 'signal',
])

/**
 * Get a comparator function for sorting companies by a specific key, or `undefined` for a key this
 * does not order.
 */
export function getCompanyComparator(key: string) {
  if (!COMPANY_SORT_KEYS.has(key)) return undefined
  return (a: CompanySignalDto, b: CompanySignalDto): number => {
    switch (key) {
      case 'company':
        return (a.CompanyName || '').localeCompare(b.CompanyName || '')
      case 'industry':
        return (a.Industry || '').localeCompare(b.Industry || '')
      case 'size':
        return (a.StaffCountRange || '').localeCompare(b.StaffCountRange || '')
      case 'lastConversion':
        return (a.LastConversionAt || '').localeCompare(b.LastConversionAt || '')
      case 'leads':
        return a.LeadCount - b.LeadCount
      case 'signal':
        return a.SignalScore - b.SignalScore
      default:
        return 0
    }
  }
}
